import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getTokenFromStorage } from '@/utils/auth';
import {
  getCampaignsSearch,
  type ClientCampaign,
  type ClientCampaignsSearchResponse,
  type ClientCampaignSortBy,
  type GetCampaignsSearchParams,
} from '@/api/beatlyApi';

/** Max campaigns pulled per team when merging "All teams" (then sorted + paginated client-side). */
const ALL_TEAMS_FETCH_PAGE_SIZE = 200;

/** Filters supported server-side (beatly-api-v2 GET .../campaigns/search). */
export type CampaignsListFilters = Pick<
  GetCampaignsSearchParams,
  'search' | 'brandName' | 'brandId' | 'listStatus' | 'budgetFilter' | 'sortBy' | 'platform'
>;

function sortMergedCampaigns(list: ClientCampaign[], sortBy: ClientCampaignSortBy): ClientCampaign[] {
  const out = [...list];
  const amount = (c: ClientCampaign) => Number(c.budget?.amount ?? 0);
  const dateTs = (c: ClientCampaign) => new Date(c.date_start ?? 0).getTime();
  const name = (c: ClientCampaign) => (c.name ?? '').toLowerCase();
  const brand = (c: ClientCampaign) => (c.brand_name ?? '').toLowerCase();

  switch (sortBy) {
    case 'newest':
      out.sort((a, b) => dateTs(b) - dateTs(a) || brand(a).localeCompare(brand(b)));
      break;
    case 'oldest':
      out.sort((a, b) => dateTs(a) - dateTs(b) || brand(a).localeCompare(brand(b)));
      break;
    case 'budget-high':
      out.sort((a, b) => amount(b) - amount(a) || dateTs(b) - dateTs(a));
      break;
    case 'budget-low':
      out.sort((a, b) => amount(a) - amount(b) || dateTs(a) - dateTs(b));
      break;
    case 'name-az':
      out.sort((a, b) => name(a).localeCompare(name(b)) || dateTs(b) - dateTs(a));
      break;
    case 'name-za':
      out.sort((a, b) => name(b).localeCompare(name(a)) || dateTs(b) - dateTs(a));
      break;
    default:
      break;
  }
  return out;
}

function mergeCampaignsFromTeams(teamsResponses: ClientCampaignsSearchResponse[]): ClientCampaign[] {
  const byId = new Map<string, ClientCampaign>();
  for (const r of teamsResponses) {
    for (const c of r.campaigns ?? []) {
      if (c?.campaign_id && !byId.has(c.campaign_id)) {
        byId.set(c.campaign_id, c);
      }
    }
  }
  return [...byId.values()];
}

export interface UseCampaignsParams {
  pageNumber?: number;
  pageSize?: number;
  /**
   * 'all' = merge campaigns from allTeamIds (via team_id on search).
   * Otherwise the team's team_id (must match a team the user belongs to).
   */
  teamScope?: 'all' | string;
  /** Team ids to merge when teamScope === 'all' (typically from GET /teams/search). */
  allTeamIds?: string[];
  /** Pass-through to API; omit fields for no filter. */
  filters?: CampaignsListFilters;
  /** If true, only compute accurate count_total (campaigns can be empty). */
  countOnly?: boolean;
}

function filtersKey(f: CampaignsListFilters | undefined): string {
  if (!f || Object.keys(f).length === 0) {
    return '';
  }
  return JSON.stringify({
    search: f.search ?? '',
    brandName: f.brandName ?? '',
    brandId: f.brandId ?? '',
    listStatus: f.listStatus ?? '',
    budgetFilter: f.budgetFilter ?? '',
    sortBy: f.sortBy ?? 'newest',
    platform: f.platform ?? '',
  });
}

/**
 * Fetches paginated campaigns for the current client.
 * Same API as beatly-web-app: GET /app/v2/client/campaigns/search
 */
export function useCampaigns(params: UseCampaignsParams = {}) {
  const { pageNumber = 0, pageSize = 10, teamScope = 'all', allTeamIds = [], filters = {}, countOnly = false } = params;
  const sortBy: ClientCampaignSortBy = filters.sortBy ?? 'newest';
  const { user } = useAuth();
  const token = getTokenFromStorage();

  const isAllTeams = teamScope === 'all';
  const specificTeamId = !isAllTeams ? teamScope : '';

  const effectiveAllTeamIds = useMemo(() => {
    if (allTeamIds.length > 0) return allTeamIds;
    const cur = user?.team?.team_id;
    return cur ? [cur] : [];
  }, [allTeamIds, user?.team?.team_id]);

  const enabled = Boolean(
    token &&
      user?.is_client &&
      (isAllTeams ? effectiveAllTeamIds.length > 0 : Boolean(specificTeamId))
  );

  const sortedIdsKey = useMemo(() => [...effectiveAllTeamIds].sort().join(','), [effectiveAllTeamIds]);
  const fKey = filtersKey(filters);

  const queryKey = isAllTeams
    ? ['client', 'campaigns', 'search', 'all', sortedIdsKey, pageNumber, pageSize, fKey, countOnly]
    : ['client', 'campaigns', 'search', specificTeamId, pageNumber, pageSize, fKey, countOnly];

  const searchPayload = useMemo(
    (): Pick<
      GetCampaignsSearchParams,
      'search' | 'brandName' | 'brandId' | 'listStatus' | 'budgetFilter' | 'sortBy' | 'platform'
    > => ({
      search: filters.search,
      brandName: filters.brandName,
      brandId: filters.brandId,
      listStatus: filters.listStatus,
      budgetFilter: filters.budgetFilter,
      sortBy,
      platform: filters.platform,
    }),
    [
      filters.search,
      filters.brandName,
      filters.brandId,
      filters.listStatus,
      filters.budgetFilter,
      filters.platform,
      sortBy,
    ]
  );

  const { data, isLoading, error, isError } = useQuery({
    queryKey,
    queryFn: async (): Promise<ClientCampaignsSearchResponse> => {
      const t = token ?? '';
      if (isAllTeams) {
        if (countOnly) {
          const totals = await Promise.all(
            effectiveAllTeamIds.map(async (tid) => {
              const r = await getCampaignsSearch(
                {
                  pageNumber: 0,
                  pageSize: 1,
                  teamId: tid,
                  ...searchPayload,
                },
                t
              );
              return r.count_total ?? 0;
            })
          );
          const total = totals.reduce((acc, n) => acc + n, 0);
          return {
            page_number: pageNumber,
            page_size: pageSize,
            count_total: total,
            has_more: false,
            campaigns: [],
          };
        }

        const results = await Promise.all(
          effectiveAllTeamIds.map((tid) =>
            getCampaignsSearch(
              {
                pageNumber: 0,
                pageSize: ALL_TEAMS_FETCH_PAGE_SIZE,
                teamId: tid,
                ...searchPayload,
              },
              t
            )
          )
        );
        const merged = sortMergedCampaigns(mergeCampaignsFromTeams(results), sortBy);
        const total = merged.length;
        const start = pageNumber * pageSize;
        const slice = merged.slice(start, start + pageSize);
        const hasMore = start + pageSize < total;
        return {
          page_number: pageNumber,
          page_size: pageSize,
          count_total: total,
          has_more: hasMore,
          campaigns: slice,
        };
      }
      return getCampaignsSearch(
        {
          pageNumber: countOnly ? 0 : pageNumber,
          pageSize: countOnly ? 1 : pageSize,
          teamId: specificTeamId,
          ...searchPayload,
        },
        t
      );
    },
    enabled,
  });

  return {
    data: data ?? {
      campaigns: [],
      page_number: 0,
      page_size: pageSize,
      count_total: 0,
      has_more: false,
    },
    isLoading,
    error,
    isError,
    isEnabled: enabled,
  };
}
